/**
 * Classification of hls.js `ERROR` event payloads.
 *
 * hls.js reports every failure through one event with a loosely typed payload:
 * the HTTP status lives in a different field depending on the loader, and the
 * only thing distinguishing a DRM rejection from a missing playlist is the
 * `details` string. This module answers "what does this error mean"; deciding
 * what to do about it (recover, reload, surface a message) stays with the
 * player component.
 */

export type HlsErrorData = {
  details?: string;
  url?: string;
  context?: {
    url?: string;
  };
  response?: {
    code?: number;
    status?: number;
  };
  // hls.js 1.7 types this as NullableNetworkDetails (null when the loader
  // produced no response object).
  networkDetails?: {
    status?: number;
    response?: {
      status?: number;
    };
  } | null;
};

/**
 * hls.js `ErrorDetails` values for a playlist request that never produced a
 * usable playlist. String literals rather than the `Hls.ErrorDetails` enum so
 * this stays importable without pulling hls.js in; the enum members resolve to
 * exactly these strings.
 */
const PLAYLIST_LOAD_ERROR_DETAILS = [
  'manifestLoadError',
  'levelLoadError',
  'audioTrackLoadError',
];

/**
 * hls.js `ErrorDetails` values for the multivariant manifest request. Until it
 * parses there is no level to resume, so `startLoad()` cannot recover from
 * these - see `classifyFatalNetworkError`.
 */
const MANIFEST_LOAD_ERROR_DETAILS = [
  'manifestLoadError',
  'manifestLoadTimeOut',
];

/** hls.js `ErrorDetails` values that indicate an EME/DRM failure. */
const DRM_ERROR_DETAILS = [
  'keySystemNoKeys',
  'keySystemNoAccess',
  'keySystemNoSession',
  'keySystemNoConfiguredLicense',
  'keySystemLicenseRequestFailed',
  'keySystemServerCertificateRequestFailed',
  'keySystemServerCertificateUpdateFailed',
  'keySystemSessionUpdateFailed',
  'keySystemStatusOutputRestricted',
  'keySystemStatusInternalError',
  'keyLoadError',
  'keyLoadTimeOut',
  'keySystemError',
];

/**
 * Pull the HTTP status out of an hls.js error payload. Which field carries it
 * depends on the loader in use, so check every shape hls.js populates.
 */
export function getHlsErrorStatusCode(
  data: HlsErrorData | null | undefined,
): number | null {
  return data?.response?.code ??
    data?.response?.status ??
    data?.networkDetails?.status ??
    data?.networkDetails?.response?.status ??
    null;
}

/**
 * True when a playlist request failed with a 404, i.e. the source is expected
 * to be playable but its playlist is not on the CDN yet.
 *
 * The live control plane marks a stream playable before the packager has
 * written its playlists: the API flips a stream to Running (and a preview
 * player renders Created/Scheduled streams) while the CDN still 404s
 * `live.m3u8`. hls.js has no recovery for this - after a failed multivariant
 * manifest load there is no level to resume, so `startLoad()` requests nothing
 * and playback dead-ends until the page is reloaded.
 *
 * Deliberately narrow: other failures (403 token rejections, 5xx, timeouts)
 * keep hls.js's own handling so a genuinely broken source cannot turn into an
 * endless reload loop.
 */
export function isPlaylistNotFoundError(
  data: HlsErrorData | null | undefined,
): boolean {
  if (!data?.details) return false;
  if (!PLAYLIST_LOAD_ERROR_DETAILS.includes(data.details)) return false;
  return getHlsErrorStatusCode(data) === 404;
}

/**
 * True when a playlist request was refused with a 403. For a live source this
 * can be the attached token lapsing - a viewer clock running behind the API's,
 * or a poll interval longer than the refresh margin - while the status poll
 * already holds a newer signing. The caller decides whether that applies;
 * anything else keeps the regular refusal handling.
 */
export function isPlaylistForbiddenError(
  data: HlsErrorData | null | undefined,
): boolean {
  if (!data?.details) return false;
  if (!PLAYLIST_LOAD_ERROR_DETAILS.includes(data.details)) return false;
  return getHlsErrorStatusCode(data) === 403;
}

/**
 * The playlist URL a load error is about, when hls.js reports one.
 *
 * `isPlaylistNotFoundError` covers the multivariant manifest *and* the level
 * and audio-track playlists it references, so "the playlist that 404'd" is not
 * necessarily the source URL. hls.js puts it on `url` for most loaders and on
 * the loader context for others; check both.
 */
export function getHlsErrorUrl(
  data: HlsErrorData | null | undefined,
): string | null {
  const url = data?.url ?? data?.context?.url;

  return typeof url === 'string' && url.length > 0 ? url : null;
}

/** True when the error came from the EME/DRM pipeline rather than delivery. */
export function isDrmError(errorDetails: string | null | undefined): boolean {
  if (!errorDetails) return false;
  return DRM_ERROR_DETAILS.includes(errorDetails);
}

/**
 * What a fatal hls.js network error means for the viewer.
 *
 * - `forbidden`: the CDN refused the request (401/403) - the library's security
 *   settings (referrer blocking, token authentication) rejected this viewer.
 * - `not-found`: the resource is gone (404/410) - the video was deleted or its
 *   playlists were never written.
 * - `unavailable`: nothing hls.js can resume from - another 4xx, or the
 *   multivariant manifest failed or timed out before any level was known.
 * - `recoverable`: a fragment or level request failed after the manifest
 *   parsed with a status hls.js does retry (5xx, timeout, offline), so
 *   `startLoad()` has something to resume.
 */
export type FatalNetworkErrorKind =
  | 'forbidden'
  | 'not-found'
  | 'unavailable'
  | 'recoverable';

/**
 * Classify a fatal `NETWORK_ERROR` payload by whether `startLoad()` can do
 * anything about it, and if not, why the request failed.
 *
 * hls.js never retries a 4xx (`retryForHttpStatus`), so a fatal 4xx means the
 * request was rejected outright; calling `startLoad()` only re-issues it and
 * fails the same way, with no event the viewer would ever see (STREAM-1120).
 * After a failed multivariant manifest `startLoad()` is a no-op regardless of
 * status - see [[hlsjs-manifest-404-no-recovery]] - so those are terminal too.
 */
/**
 * True for a failure of the multivariant manifest request itself. hls.js has
 * no level to resume from afterwards, so only a reload can recover it.
 */
export function isManifestLoadError(
  errorDetails: string | null | undefined,
): boolean {
  if (!errorDetails) return false;
  return MANIFEST_LOAD_ERROR_DETAILS.includes(errorDetails);
}

export function classifyFatalNetworkError(
  data: HlsErrorData | null | undefined,
): FatalNetworkErrorKind {
  const status = getHlsErrorStatusCode(data);

  if (status === 401 || status === 403) return 'forbidden';
  if (status === 404 || status === 410) return 'not-found';
  if (status !== null && status >= 400 && status < 500) return 'unavailable';

  if (isManifestLoadError(data?.details)) return 'unavailable';

  return 'recoverable';
}
